import { toast } from 'react-toastify';
import { UseMutationResult } from 'react-query';

import {
    AvailableReportsData,
    DistrictReportData,
    ReportConfig,
    ReportOption,
    TeacherReportData,
} from '../types';

interface DistrictPMReportDependencies {
    getDistrictPMReportMutation: UseMutationResult<
        any,
        Error,
        { districtId: number; districtName?: string }
    >;
}

interface TeacherPMReportDependencies {
    getTeacherPMReportMutation: UseMutationResult<
        any,
        Error,
        { teacherId: number; teacherName?: string }
    >;
}

const notifyPMReportResult = (result: any, label: string) => {
    if (result?.response?.status === 202) {
        toast.info(`${label} progress monitoring report is being generated. It will appear in past reports when ready.`);
    } else {
        toast.error(`Failed to request progress monitoring report for ${label}.`);
    }
};

export const createDistrictPMReportConfigs = (
    availableReportsData: AvailableReportsData,
    dependencies: DistrictPMReportDependencies,
): ReportConfig[] => {
    const configs: ReportConfig[] = [];
    const districtReports = availableReportsData?.skill_level_reports?.district_reports;

    if (!districtReports) {
        return configs;
    }

    const districtOptions = Object.values(districtReports).map(
        (district: DistrictReportData) => ({
            id: district.district_id,
            label: district.district_name,
            ...district,
        }),
    );

    if (districtOptions.length > 0) {
        configs.push({
            id: 'pm_district',
            name: 'District Progress Monitoring Report',
            option_name: 'district',
            options: districtOptions,
            multiple: false,
            selections: null,
            onSubmit: async (context) => {
                const district = context.selections[0] as unknown as DistrictReportData;
                if (!district) { return; }
                const result = await dependencies.getDistrictPMReportMutation.mutateAsync({
                    districtId: district.district_id,
                    districtName: district.district_name,
                });
                notifyPMReportResult(result, district.district_name);
            },
        });
    }

    return configs;
};

export const createTeacherPMReportConfigs = (
    availableReportsData: AvailableReportsData,
    dependencies: TeacherPMReportDependencies,
): ReportConfig[] => {
    const configs: ReportConfig[] = [];
    const teacherReports = availableReportsData?.skill_level_reports?.teacher_reports;

    if (!teacherReports) {
        return configs;
    }

    const teacherOptions: ReportOption[] = Object.values(teacherReports).map(
        (teacher: TeacherReportData) => ({
            id: teacher.teacher_id,
            label: teacher.teacher_name,
            ...teacher,
        }),
    );

    if (teacherOptions.length > 0) {
        configs.push({
            id: 'pm_teacher',
            name: 'Teacher Progress Monitoring Report',
            option_name: 'teacher',
            options: teacherOptions,
            multiple: false,
            selections: null,
            onSubmit: async (context) => {
                const teacher = context.selections[0] as unknown as TeacherReportData;
                if (!teacher) { return; }
                const result = await dependencies.getTeacherPMReportMutation.mutateAsync({
                    teacherId: teacher.teacher_id,
                    teacherName: teacher.teacher_name,
                });
                notifyPMReportResult(result, teacher.teacher_name);
            },
        });
    }

    return configs;
};
